import { Lineicons, type LineiconsProps } from '@lineiconshq/react-lineicons';
import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useActionBarLoading, useActionBarSelectionRequirement } from '../action-bar-v2';
import { useExcelOperationLoadingSignal } from '@/lib/excel/operation-events';
import { cn } from '@/lib/utils';

export type LineIconData = LineiconsProps['icon'];

export interface ActionButtonV2Props {
  title?: string;
  icon: LineIconData;
  onClick?: () => unknown;
  variant?: 'primary' | 'secondary' | 'destructive';
  type?: 'button' | 'submit' | 'reset';
  form?: string;
  disabled?: boolean;
  shortcut?: string;
  loading?: boolean;
  loadingTitle?: string;
  className?: string;
}

export const ActionButtonV2: React.FC<ActionButtonV2Props> = ({
  title,
  icon,
  onClick,
  variant = 'secondary',
  type = 'button',
  form,
  disabled = false,
  shortcut,
  loading = false,
  loadingTitle,
  className
}) => {
  const resolvedTitle = loading && loadingTitle ? loadingTitle : title;
  const normalizedActionTitle = title?.trim().toLocaleLowerCase('vi-VN');
  const actionBarLoading = useActionBarLoading();
  const selectionRequirement = useActionBarSelectionRequirement(title);
  const tooltipTitle = selectionRequirement.disabled
    ? `${resolvedTitle || ''}${resolvedTitle ? ' - ' : ''}${selectionRequirement.message}`
    : shortcut
      ? `${resolvedTitle} (${shortcut})`
      : resolvedTitle;
  const isDisabled = disabled || loading || selectionRequirement.disabled;
  const shouldShowExcelOverlay = loading && /excel|xuất|kết xuất/i.test(`${title || ''} ${loadingTitle || ''}`);

  useExcelOperationLoadingSignal(
    shouldShowExcelOverlay,
    loadingTitle || 'Đang kết xuất dữ liệu...',
    `action-button-${title || 'excel'}`
  );

  const handleClick = async () => {
    if (!onClick) return;

    const result = onClick();
    if (!result || typeof (result as Promise<unknown>).then !== 'function') {
      return;
    }

    const stopLoading = actionBarLoading?.beginLoading();
    try {
      await result;
    } finally {
      stopLoading?.();
    }
  };

  const getButtonColorClass = () => {
    if (isDisabled) return 'cursor-not-allowed bg-gray-800 opacity-50 text-white';

    const normalizedTitle = title?.trim().toLowerCase();

    if (
      normalizedTitle === 'xóa' ||
      normalizedTitle === 'xoá' ||
      normalizedTitle === 'đóng' ||
      normalizedTitle === 'hủy' ||
      normalizedTitle === 'huỷ' ||
      normalizedTitle === 'close' ||
      normalizedTitle === 'cancel'
    ) {
      return 'bg-red-600 hover:bg-red-700 text-white';
    }

    if (
      normalizedTitle === 'thêm' ||
      normalizedTitle === 'sửa' ||
      normalizedTitle === 'sao chép' ||
      normalizedTitle === 'lưu'
    ) {
      return 'bg-main text-main-foreground hover:bg-main-dark';
    }

    if (
      normalizedTitle === 'in ấn' ||
      normalizedTitle === 'tìm kiếm' ||
      normalizedTitle === 'in' ||
      normalizedTitle === 'gửi xử lý' ||
      normalizedTitle === 'refresh' ||
      normalizedTitle === 'cố định cột' ||
      normalizedTitle === 'xuất excel' ||
      normalizedTitle === 'nhập excel' ||
      normalizedTitle === 'import' ||
      normalizedTitle === 'export'
    ) {
      return 'bg-main-light text-main-foreground hover:bg-main';
    }

    if (variant === 'primary') return 'bg-main text-main-foreground hover:bg-main-dark';
    if (variant === 'destructive') return 'bg-red-800 hover:bg-red-900 text-white';
    return 'bg-main text-main-foreground hover:bg-main-dark';
  };

  const buttonContent = (
    <button
      onClick={() => void handleClick()}
      type={type}
      form={form}
      disabled={isDisabled}
      data-erp-action-title={normalizedActionTitle}
      className={cn(
        'flex h-[var(--erp-command-button-height)] min-w-[70px] shrink-0 flex-col items-center justify-center rounded-[2.5px] px-3 py-0 font-semibold leading-none transition duration-75 hover:shadow-sm',
        getButtonColorClass(),
        className
      )}
    >
      <Lineicons aria-hidden='true' icon={icon} size={22} strokeWidth={1.8} />
      {resolvedTitle && <span className='mt-0.5 text-xs leading-3'>{resolvedTitle}</span>}
    </button>
  );

  if (!tooltipTitle) {
    return buttonContent;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
        <TooltipContent side='top' className='z-[99999] border-none bg-[#333] px-2 py-1 text-xs text-white'>
          {tooltipTitle}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
export default ActionButtonV2;
